import { BookOpenTextIcon, CalculatorIcon, CalendarCheckIcon, CalendarXIcon, ClipboardCheckIcon, FolderSearchIcon, LayersIcon, LayoutGridIcon, PiggyBankIcon, ShieldCheckIcon, StoreIcon, UsersIcon } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

// FINANZA PRONTA™ (Italia). Fonte unica: finanza-pronta/PRODUCTO.md — promessa = consegna (1:1 con i 6 volumi).
// Contenuto EDUCATIVO: nessuna consulenza finanziaria o di investimento.
export const PRODUCT_NAME = 'FINANZA PRONTA™';
export const heroGuarantees = ['Accesso immediato', 'Pagamento unico', 'Nessun abbonamento', 'Garanzia di 7 giorni'];
export const stats = [{
  value: '240',
  label: 'Pagine nei 6 volumi principali'
}, {
  value: '12',
  label: 'Lezioni minuto per minuto (60\') nel Volume 1'
}, {
  value: '60',
  label: 'Attività e giochi con soldi finti'
}, {
  value: '20',
  label: 'Compiti di realtà'
}, {
  value: '100',
  label: 'Giudizi pronti'
}];
export const differenceStats = [{
  value: 'Le 33 ore',
  label: 'Programmazione annuale mese per mese dentro le 33 ore di educazione civica, da settembre a giugno'
}, {
  value: 'Schede in 3 livelli',
  label: 'Ogni lezione con pagina docente e scheda alunno in 3 livelli: nessun alunno resta indietro'
}, {
  value: 'Linee guida 2024',
  label: 'In linea con le Linee guida 2024 per l\'educazione civica e i loro 4 ambiti dell\'educazione finanziaria'
}];
export const beforeItems: Array<{
  title: string;
  text: string;
}> = [{
  title: 'Una materia nuova, senza materiali',
  text: 'L\'educazione finanziaria è entrata nell\'educazione civica, ma nessuno ti ha dato lezioni pronte per la tua classe.'
}, {
  title: 'Ore a cercare schede online',
  text: 'Materiali sparsi, troppo difficili o troppo semplici, spesso pensati per altri Paesi e con importi che non tornano.'
}, {
  title: 'Kit che arrivano tardi',
  text: 'I kit di campagna si chiedono, arrivano a metà anno e coprono qualche settimana, non tutto l\'anno scolastico.'
}, {
  title: 'Classi con livelli diversi',
  text: 'C\'è chi conta ancora le monete e chi calcola già le percentuali: adattare ogni scheda richiede serate intere.'
}, {
  title: 'Paura di sbagliare i contenuti',
  text: 'Banca, IBAN, pagamenti digitali, tasse: spiegarli in modo semplice e corretto non è immediato.'
}, {
  title: 'Valutare e documentare',
  text: 'Rubriche, griglie e giudizi per il curricolo di educazione civica diventano altro lavoro dopo la scuola.'
}];
export const afterItems = ['Programmazione annuale per primaria 1ª–2ª, 3ª–5ª e secondaria, mese per mese', '12 lezioni minuto per minuto (60\') pronte da usare', 'Pagina docente e scheda alunno in 3 livelli per ogni lezione', '60 attività e giochi con soldi finti', '20 compiti di realtà, compreso il progetto «la nostra impresa»', 'Banca, pagamenti digitali e sicurezza online spiegati in modo semplice', 'Verifiche A/B con soluzioni e rubriche di valutazione', '100 giudizi pronti', 'In linea con le Linee guida 2024 per l\'educazione civica'];
export const recognizeItems: Array<{
  icon: LucideIcon;
  title: string;
  text: string;
}> = [{
  icon: CalendarXIcon,
  title: 'Non sai da dove partire',
  text: 'Devi inserire l\'educazione finanziaria nelle 33 ore di educazione civica, ma non hai una programmazione pronta?'
}, {
  icon: FolderSearchIcon,
  title: 'Non trovi materiale adatto',
  text: 'Cerchi lezioni e schede adatte all\'età dei tuoi alunni, con esempi in euro, e non le trovi mai già pronte?'
}, {
  icon: LayersIcon,
  title: 'Livelli troppo diversi',
  text: 'Ti serve la stessa attività per chi è in difficoltà e per chi corre avanti, senza preparare tre schede diverse?'
}, {
  icon: ShieldCheckIcon,
  title: 'Temi delicati da spiegare',
  text: 'Vorresti parlare di banca, carte, pagamenti online e truffe con parole semplici e corrette?'
}, {
  icon: StoreIcon,
  title: 'Vuoi qualcosa di concreto',
  text: 'Vorresti giochi con soldi finti, un mercatino di classe e compiti di realtà che coinvolgano davvero gli alunni?'
}, {
  icon: LayoutGridIcon,
  title: 'Ti serve una struttura per tutto l\'anno',
  text: 'Vorresti un percorso organizzato da settembre a giugno, con valutazione e documentazione già pronte?'
}];

export const volumes: Array<{
  number: string;
  level: string;
  classes: string;
  title: string;
  tagline: string;
  intro: string;
  items: string[];
  outro: string;
  note?: string;
  spine: string;
  rule: string;
}> = [{
  number: 'Volume 01',
  level: 'Anno',
  classes: 'Primaria 1ª–5ª e Secondaria 1ª–3ª',
  title: 'Programmazione annuale + 12 lezioni minuto per minuto',
  tagline: 'Tutto l\'anno di educazione finanziaria, organizzato mese per mese.',
  intro: 'Il primo volume ti mostra come inserire l\'educazione finanziaria nelle 33 ore di educazione civica e contiene 12 lezioni da 60 minuti, spiegate minuto per minuto, con pagina docente e scheda alunno in 3 livelli.',
  items: ['Educazione finanziaria nelle 33 ore di educazione civica', 'Programmazione annuale: primaria 1ª–2ª, 3ª–5ª e secondaria', 'Mese per mese, da settembre a giugno', 'I 4 ambiti delle Linee guida', 'Tabella di progressione', '12 lezioni minuto per minuto (60\')', 'Pagina docente + scheda alunno in 3 livelli', 'Griglia di osservazione'],
  outro: 'Dalla prima lezione («Che cos\'è il denaro? Dal baratto all\'euro») al progetto finale: il mercatino di classe.',
  spine: 'bg-[#1E2B58] text-white',
  rule: 'border-white/30'
}, {
  number: 'Volume 02',
  level: 'Denaro',
  classes: 'Primaria e Secondaria',
  title: 'Il denaro e il valore',
  tagline: 'Dalle monete al risparmio, con attività pratiche.',
  intro: 'Il secondo volume parte dalle basi: la storia del denaro, l\'euro, il valore delle cose e il risparmio, con schede in 3 livelli e soluzioni.',
  items: ['Storia del denaro', 'Euro e centesimi', 'Contare e dare il resto', 'Bisogni e desideri', 'Valore e prezzo', 'Risparmio e obiettivi', 'La paghetta', 'Dono e condivisione'],
  outro: 'Con 20 attività e giochi con soldi finti e schede in 3 livelli con soluzioni.',
  spine: 'bg-[#1F9D6B] text-white',
  rule: 'border-white/30'
}, {
  number: 'Volume 03',
  level: 'Budget',
  classes: 'Primaria e Secondaria',
  title: 'Budget e scelte di consumo',
  tagline: 'Imparare a scegliere prima di spendere.',
  intro: 'Il terzo volume porta in classe il budget personale e di classe, la spesa consapevole e il confronto dei prezzi, con problemi dai calcoli verificati.',
  items: ['Budget personale e di classe', 'Entrate e uscite', 'La lista della spesa', 'Confronto prezzi e prezzo al kg', 'Sconti e percentuali', 'Pubblicità e influencer', 'Acquisti d\'impulso', 'Sostenibilità e riuso'],
  outro: 'Con 20 attività e problemi con calcoli verificati.',
  spine: 'bg-[#F2B134] text-[#1E2B58]',
  rule: 'border-[#1E2B58]/20'
}, {
  number: 'Volume 04',
  level: 'Banca',
  classes: 'Primaria e Secondaria',
  title: 'Banca, pagamenti digitali e sicurezza',
  tagline: 'Conto, carte e pagamenti online, spiegati semplice.',
  intro: 'Il quarto volume spiega come funzionano la banca e i pagamenti digitali e come riconoscere le truffe online, con scenari pronti da discutere in classe.',
  items: ['Il conto corrente', 'Carta di debito e prepagata', 'Il bonifico e l\'IBAN spiegato semplice', 'Pagamenti con lo smartphone', 'PIN e password', 'Truffe online: phishing e falsi premi', 'Acquisti nei giochi', 'Le regole d\'oro'],
  outro: 'Con 20 attività e scenari per allenare scelte sicure.',
  spine: 'bg-[#FF8A7A] text-[#1E2B58]',
  rule: 'border-[#1E2B58]/20'
}, {
  number: 'Volume 05',
  level: 'Lavoro',
  classes: 'Primaria e Secondaria',
  title: 'Lavoro, impresa e futuro',
  tagline: 'Dal valore del lavoro all\'impresa di classe.',
  intro: 'Il quinto volume collega il denaro al lavoro, all\'impresa e ai beni comuni, con esempi semplici e il progetto «la nostra impresa».',
  items: ['Mestieri e stipendio', 'L\'impresa di classe', 'Costi e ricavi', 'L\'interesse semplice spiegato con esempi', 'Prestiti e debiti', 'Tasse e beni comuni', 'Assicurazione e rischio', 'La previdenza in parole semplici'],
  outro: 'Con 20 compiti di realtà, compreso il progetto «la nostra impresa».',
  spine: 'bg-[#3A86C8] text-white',
  rule: 'border-white/30'
}, {
  number: 'Volume 06',
  level: 'Valutazione',
  classes: 'Primaria e Secondaria',
  title: 'Valutazione e documentazione',
  tagline: 'Valutare e documentare senza perdere le serate.',
  intro: 'Il sesto volume raccoglie rubriche, griglie, verifiche e tutto quello che serve per documentare il percorso nel curricolo di educazione civica, con 100 giudizi pronti.',
  items: ['Rubriche per la primaria con giudizi sintetici', 'Rubriche per la secondaria in decimi', 'Griglie di osservazione', 'Autovalutazione', 'Verifiche A/B per fascia con soluzioni', 'Compiti di realtà con rubrica', 'Documentazione per il curricolo di educazione civica', '100 giudizi pronti'],
  outro: 'Un passo finale completo per valutare e documentare con serenità.',
  note: 'Materiale didattico indipendente e contenuto educativo: non è una pubblicazione ufficiale del Ministero dell\'Istruzione e del Merito e non costituisce consulenza finanziaria.',
  spine: 'bg-[#6B4FA0] text-white',
  rule: 'border-white/30'
}];
export const volumesIntro = {
  title: '6 volumi digitali per Primaria e Secondaria',
  text: 'Un sistema organizzato con programmazione, lezioni pronte, schede in 3 livelli, attività e giochi, compiti di realtà e valutazione. Apri il volume, scegli la lezione e stampa. Meno preparazione. Più tempo per i tuoi alunni.'
};
export const packItems: Array<{
  icon: LucideIcon;
  title: string;
  text: string;
}> = [{
  icon: BookOpenTextIcon,
  title: 'Lezioni pronte',
  text: '12 lezioni minuto per minuto nel Volume 1, con obiettivi, materiali, tempi e soluzioni.'
}, {
  icon: LayersIcon,
  title: 'Schede in 3 livelli',
  text: 'Ogni scheda alunno ha 3 livelli sulla stessa pagina: per la classe intera, senza preparare tre versioni.'
}, {
  icon: PiggyBankIcon,
  title: 'Attività e giochi',
  text: '60 attività e giochi con soldi finti: contare, dare il resto, risparmiare, fare la spesa.'
}, {
  icon: CalculatorIcon,
  title: 'Esempi in euro verificati',
  text: 'Problemi ed esempi in euro con calcoli controllati e soluzioni pronte.'
}, {
  icon: ClipboardCheckIcon,
  title: 'Valutazione pronta',
  text: 'Verifiche A/B con soluzioni, rubriche per primaria e secondaria e 100 giudizi pronti.'
}, {
  icon: CalendarCheckIcon,
  title: 'Programmazione annuale',
  text: 'Mese per mese da settembre a giugno dentro le 33 ore di educazione civica, con i 4 ambiti delle Linee guida.'
}];
export const pageTypes = ['Programmazione annuale', 'Lezioni minuto per minuto', 'Schede alunno in 3 livelli', 'Griglie di osservazione'];
export const bonuses: Array<{
  title: string;
  text: string;
  items: string[];
  benefit: string;
}> = [{
  title: 'Il Mercatino di Classe',
  text: 'Il kit completo per trasformare l\'aula in un piccolo mercato.',
  items: ['Soldi finti da stampare (euro giocattolo)', 'Cartellini prezzi', 'Scontrini', 'Ruoli e regole', 'In 3 livelli'],
  benefit: 'Il progetto più amato dagli alunni, già pronto da stampare.'
}, {
  title: '50 Warm-up finanziari da 5 minuti',
  text: 'Per iniziare la lezione con la classe già attiva.',
  items: ['Domande flash', 'Indovinelli', 'Mini-sfide di calcolo', 'Con soluzioni'],
  benefit: 'Cinque minuti e la classe è subito concentrata.'
}, {
  title: 'Comunicazione con le famiglie',
  text: 'Lettere pronte per coinvolgere le famiglie nel percorso.',
  items: ['Presentazione del percorso', 'La paghetta a casa', 'Sfida del risparmio in famiglia', 'Sicurezza online', 'Il mercatino'],
  benefit: 'Basta compilare e inviare.'
}, {
  title: 'Poster per l\'aula',
  text: '20 poster da stampare e appendere in classe.',
  items: ['Bisogni e desideri', 'Regole d\'oro della sicurezza online', 'Il budget in 4 passi', 'Le monete e le banconote', 'Le parole della finanza'],
  benefit: 'Un\'aula ordinata e visiva in pochi minuti.'
}, {
  title: 'Lezioni per il supplente',
  text: '15 lezioni di educazione finanziaria senza preparazione.',
  items: ['15 lezioni pronte', 'Istruzioni passo per passo', 'Nessuna preparazione', 'Utilizzabili da qualsiasi docente'],
  benefit: 'Il percorso continua anche quando non ci sei.'
}];
export const BONUS_VALUE = '19 €';
export const steps: Array<{
  title: string;
  text: string;
}> = [{
  title: 'Apri FINANZA PRONTA™',
  text: 'Scarica il pacchetto e apri il volume che ti serve: programmazione, lezioni, attività o valutazione.'
}, {
  title: 'Scegli la lezione',
  text: 'Segui la programmazione del mese e trova la lezione adatta alla tua classe.'
}, {
  title: 'Preparati in pochi minuti',
  text: 'Leggi la pagina docente e stampa la scheda alunno nel livello giusto.'
}, {
  title: 'Entra in aula',
  text: 'Fai la lezione minuto per minuto, poi osserva e valuta con griglie e rubriche pronte.'
}];
export const audienceItems = ['Insegni nella scuola Primaria o nella Secondaria di primo grado', 'Devi svolgere l\'educazione finanziaria nelle ore di educazione civica', 'Sei referente di educazione civica e vuoi un percorso comune', 'Insegni matematica, tecnologia o lettere e vuoi esempi concreti', 'Vuoi schede in 3 livelli per classi eterogenee', 'Vuoi attività e giochi con soldi finti', 'Vuoi rubriche e giudizi già pronti', 'Non sei esperto di finanza ma vuoi spiegarla bene'];
export const timeValueTasks = ['Cercare e adattare lezioni e schede', 'Preparare versioni diverse per livello', 'Inventare problemi con importi corretti', 'Organizzare il percorso nelle 33 ore', 'Preparare griglie e giudizi'];
export const offerRows: Array<{
  label: string;
  detail: string;
}> = [{
  label: '6 volumi digitali – 240 pagine',
  detail: 'Programmazione e 12 lezioni, il denaro e il valore, budget e scelte di consumo, banca e sicurezza, lavoro e futuro, valutazione'
}, {
  label: '5 bonus senza costi aggiuntivi',
  detail: 'Il Mercatino di Classe, 50 warm-up finanziari, comunicazione con le famiglie, poster per l\'aula, lezioni per il supplente'
}, {
  label: 'Primaria 1ª–5ª e Secondaria 1ª–3ª',
  detail: 'Schede in 3 livelli, 60 attività e giochi, 20 compiti di realtà, 100 giudizi pronti'
}];
export const offerIncludes = ['Accesso immediato', 'File PDF da salvare', 'PDF da stampare', 'Nessun prodotto fisico spedito'];
export const PRICE = '19 €';
export const PRICE_OLD = '97 €';

// Solo recensioni autentiche di FINANZA PRONTA. La sezione resta nascosta finché è vuota.
export const testimonials: Array<{
  quote: string;
  name: string;
  location: string;
}> = [];
export const faqs = [{
  q: 'Come ricevo il materiale?',
  a: 'FINANZA PRONTA™ è un prodotto digitale. Dopo l\'acquisto ricevi l\'accesso ai file PDF da scaricare e salvare. Puoi usarli su computer, tablet o smartphone e stampare le pagine che ti servono.'
}, {
  q: 'Per quali classi è?',
  a: 'Per la Primaria (1ª–5ª) e la Secondaria di primo grado (1ª–3ª). La programmazione annuale è divisa per fasce (1ª–2ª, 3ª–5ª e secondaria) e ogni scheda alunno ha 3 livelli.'
}, {
  q: 'Come si inserisce nelle 33 ore di educazione civica?',
  a: 'Il Volume 1 spiega come inserire l\'educazione finanziaria nelle 33 ore annuali di educazione civica e propone una programmazione mese per mese, da settembre a giugno, organizzata sui 4 ambiti delle Linee guida.'
}, {
  q: 'È in linea con le Linee guida?',
  a: 'Sì. Il percorso è pensato in linea con le Linee guida 2024 per l\'educazione civica, che includono l\'educazione finanziaria (Legge 21/2024). È un materiale didattico indipendente, non una pubblicazione ufficiale del Ministero.'
}, {
  q: 'Non sono esperto di finanza. Posso usarlo?',
  a: 'Sì. Ogni lezione è spiegata minuto per minuto, con obiettivi, materiali, tempi e soluzioni. I temi sono spiegati con parole semplici ed esempi in euro.'
}, {
  q: 'Si parla di investimenti o di criptovalute?',
  a: 'No. FINANZA PRONTA™ è un contenuto educativo per la scuola: denaro, risparmio, budget, scelte di consumo, banca, sicurezza online, lavoro e beni comuni. Non contiene consigli di investimento.'
}, {
  q: 'Ci sono attività pratiche?',
  a: 'Sì. Trovi 60 attività e giochi con soldi finti e 20 compiti di realtà. Il bonus «Il Mercatino di Classe» contiene soldi finti da stampare, cartellini prezzi e scontrini.'
}, {
  q: 'Ci sono verifiche, rubriche e giudizi?',
  a: 'Sì. Trovi verifiche A/B con soluzioni, rubriche con giudizi sintetici per la primaria e in decimi per la secondaria, griglie di osservazione, autovalutazione e 100 giudizi pronti.'
}, {
  q: 'Devo stampare tutto?',
  a: 'No. Scegli tu quali pagine stampare e puoi usare parte del materiale in digitale, anche alla LIM.'
}, {
  q: 'I 5 bonus sono inclusi?',
  a: `Sì. Tutti e cinque i bonus sono inclusi senza costi aggiuntivi – tutto a ${PRICE}.`
}, {
  q: 'È un abbonamento?',
  a: 'No. Paghi una sola volta e ricevi l\'accesso al materiale digitale. Nessun costo ricorrente.'
}, {
  q: 'C\'è una garanzia?',
  a: 'Sì. FINANZA PRONTA™ ha una garanzia di soddisfazione di 7 giorni. Se il materiale non soddisfa le tue aspettative, puoi contattarci e chiedere il rimborso entro il periodo di garanzia, secondo le condizioni.'
}];
